import { useMemo } from 'react'
import {
  TRANSACTION_DTYPE,
  CHARGE_TRANSACTION_DTYPE,
  ADJUSTMENT_TRANSACTION_DTYPE,
  LEDGER_ENTRY_TYPE,
  APPLIED_PAYMENT_TYPES,
} from 'Data/payments.js'
import { useDataValue } from 'Simple/Data.js'

let DTYPE_TO_LEDGER_ENTRY_TYPE = /** @type {const} */ ({
  [TRANSACTION_DTYPE.ChargeTransfer]: LEDGER_ENTRY_TYPE.CHARGE_TRANSFER,
  [TRANSACTION_DTYPE.TransferCharge]: LEDGER_ENTRY_TYPE.TRANSFER_CHARGE,
  [TRANSACTION_DTYPE.InstallmentCharge]: LEDGER_ENTRY_TYPE.INSTALLMENT_CHARGE,
  [TRANSACTION_DTYPE.PaymentPlan]: LEDGER_ENTRY_TYPE.PAYMENT_PLAN,
  [TRANSACTION_DTYPE.PaymentPlanRollback]:
    LEDGER_ENTRY_TYPE.PAYMENT_PLAN_ROLLBACK,
  [TRANSACTION_DTYPE.Payment]: LEDGER_ENTRY_TYPE.APPLIED_PAYMENT,
  [TRANSACTION_DTYPE.UnappliedPayment]: LEDGER_ENTRY_TYPE.UNAPPLIED_PAYMENT,
  [TRANSACTION_DTYPE.DiscountReverseAdjustment]: LEDGER_ENTRY_TYPE.ADJUSTMENT,
})

let LEDGER_ENTRY_TYPE_ORDER = [
  LEDGER_ENTRY_TYPE.CHARGE,
  LEDGER_ENTRY_TYPE.INSTALLMENT_CHARGE,
  LEDGER_ENTRY_TYPE.ADJUSTMENT,
  LEDGER_ENTRY_TYPE.TRANSFER_CHARGE,
  LEDGER_ENTRY_TYPE.CHARGE_TRANSFER,
  LEDGER_ENTRY_TYPE.PAYMENT_PLAN,
  LEDGER_ENTRY_TYPE.PAYMENT_PLAN_ROLLBACK,
  LEDGER_ENTRY_TYPE.APPLIED_PAYMENT,
  LEDGER_ENTRY_TYPE.UNAPPLIED_PAYMENT,
  LEDGER_ENTRY_TYPE.PAYMENT,
  LEDGER_ENTRY_TYPE.NOTE,
]

export default function useDataTransform(props, data) {
  let order = useDataValue({
    viewPath: props.viewPath,
    context: 'tab',
    path: 'selected.ledger.order',
  })

  return useMemo(() => {
    if (!data) return data

    let result = []
    for (let transaction of data.transactions) {
      if (CHARGE_TRANSACTION_DTYPE.includes(transaction.dtype)) {
        if (
          result.length > 0 &&
          canAggregateTo(
            result[result.length - 1],
            LEDGER_ENTRY_TYPE.CHARGE,
            transaction
          )
        ) {
          // an aggregation of charges it's already started, appending to it
          result[result.length - 1].transactions.push(transaction)
        } else {
          // start a new aggregation entry for the charges
          result.push({
            type: LEDGER_ENTRY_TYPE.CHARGE,
            id: transaction.id,
            created_at: transaction.sys_created,
            sort_date: transaction.effective_date ?? transaction.sys_created,
            created_by: transaction.sys_created_by,
            receivable_id: transaction.receivable_id,
            created_by_user: transaction.created_by_user,
            payment_account: transaction.receivable.payment_account,
            location: transaction.receivable.organization,
            transactions: [transaction],
          })
        }
      } else if (ADJUSTMENT_TRANSACTION_DTYPE.includes(transaction.dtype)) {
        if (
          result.length > 0 &&
          canAggregateTo(
            result[result.length - 1],
            LEDGER_ENTRY_TYPE.ADJUSTMENT,
            transaction
          )
        ) {
          // an aggregation of adjustments it's already started, appending to it
          result[result.length - 1].transactions.push(transaction)
        } else {
          // start a new aggregation entry for the adjustments
          result.push({
            type: LEDGER_ENTRY_TYPE.ADJUSTMENT,
            id: transaction.id,
            created_at: transaction.sys_created,
            sort_date: transaction.effective_date ?? transaction.sys_created,
            created_by: transaction.sys_created_by,
            receivable_id: transaction.receivable_id,
            created_by_user: transaction.created_by_user,
            payment_account: transaction.receivable.payment_account,
            location: transaction.receivable.organization,
            transactions: [transaction],
          })
        }
      } else if (transaction.dtype === TRANSACTION_DTYPE.Payment) {
        result.push({
          id: transaction.id,
          // only applied payments should be returned at this stage
          type: LEDGER_ENTRY_TYPE.APPLIED_PAYMENT,
          created_at: transaction.sys_created,
          sort_date: transaction.effective_date ?? transaction.sys_created,
          created_by: transaction.sys_created_by,
          created_by_user: transaction.created_by_user,
          payment_account:
            transaction.receivable?.payment_account ??
            transaction.applied_payment?.payment?.account ??
            transaction.unapplied_payment?.applied_payment?.payment
              ?.payment_account,
          location:
            transaction.receivable?.organization ??
            transaction.applied_payment?.payment?.organization ??
            transaction.unapplied_payment?.applied_payment?.payment
              ?.organization,
          receivable_id: transaction.receivable_id ?? null,
          transaction,
        })
      } else {
        // installment's created date is when payment plan was accepted, so not very useful
        let created_at =
          transaction.dtype === TRANSACTION_DTYPE.InstallmentCharge
            ? transaction.receivable.due_date
            : (transaction.effective_date ?? transaction.sys_created)
        result.push({
          id: transaction.id,
          type: DTYPE_TO_LEDGER_ENTRY_TYPE[transaction.dtype],
          created_at: created_at,
          sort_date: created_at,
          created_by: transaction.sys_created_by,
          created_by_user: transaction.created_by_user,
          payment_account:
            transaction.receivable?.payment_account ??
            transaction.applied_payment?.payment?.account ??
            transaction.unapplied_payment?.applied_payment?.payment
              ?.payment_account,
          location:
            transaction.receivable?.organization ??
            transaction.applied_payment?.payment?.organization ??
            transaction.unapplied_payment?.applied_payment?.payment
              ?.organization,
          receivable_id: transaction.receivable_id ?? null,
          transaction,
        })
      }
    }

    for (let transaction of data.payment_transactions) {
      let type
      switch (transaction.applied_payment.applied_type) {
        case APPLIED_PAYMENT_TYPES.TO_RECEIVABLE:
          type = LEDGER_ENTRY_TYPE.APPLIED_PAYMENT
          break
        case APPLIED_PAYMENT_TYPES.REFUND:
          type = LEDGER_ENTRY_TYPE.REFUND
          break
        case APPLIED_PAYMENT_TYPES.PAYMENT_CORRECTION:
          type = LEDGER_ENTRY_TYPE.PAYMENT_CORRECTION
          break
        case APPLIED_PAYMENT_TYPES.TO_PAYMENT:
          type = LEDGER_ENTRY_TYPE.TRANSFER_CREDIT
          break
        default:
          type = DTYPE_TO_LEDGER_ENTRY_TYPE[transaction.dtype]
      }
      result.push({
        id: transaction.id,
        type,
        created_at: transaction.sys_created,
        sort_date: transaction.effective_date ?? transaction.sys_created,
        created_by: transaction.sys_created_by,
        created_by_user: transaction.created_by_user,
        payment_account:
          transaction.receivable?.payment_account ??
          transaction.applied_payment?.payment?.account ??
          transaction.unapplied_payment?.applied_payment?.payment
            ?.payment_account,
        location:
          transaction.receivable?.organization ??
          transaction.applied_payment?.payment?.organization ??
          transaction.unapplied_payment?.applied_payment?.payment?.organization,
        receivable_id: transaction.receivable_id ?? null,
        transaction,
      })
    }

    result.push(
      ...data.notes.map(item => ({
        id: item.id,
        type: LEDGER_ENTRY_TYPE.NOTE,
        created_at: item.sys_created,
        sort_date: item.sys_created,
        created_by_user: item.author,
        location: null,
        payment_account: null,
        note: item,
      }))
    )
    result.push(
      ...data.payments.map(item => ({
        id: item.id,
        type: LEDGER_ENTRY_TYPE.PAYMENT,
        created_at: item.sys_created,
        sort_date: item.sys_created,
        created_by_user: item.sys_created_by,
        payment_account: item.account,
        location: item.organization,
        payment: item,
      }))
    )

    // Sort by sort_date, then by entry type order
    // Transactions use effective_date as sort_date; notes and payments use sys_created
    result.sort((a, b) => {
      let dateComparison =
        order === 'asc'
          ? new Date(a.sort_date) - new Date(b.sort_date)
          : new Date(b.sort_date) - new Date(a.sort_date)
      if (dateComparison !== 0) return dateComparison

      let getOrder = type => {
        let i = LEDGER_ENTRY_TYPE_ORDER.indexOf(type)
        return i === -1 ? 99 : i
      }
      return getOrder(a.type) - getOrder(b.type)
    })

    return result
  }, [data, order])
}

function canAggregateTo(last, type, transaction) {
  return (
    last.type === type &&
    last.created_by === transaction.sys_created_by &&
    last.receivable_id === transaction.receivable_id
  )
}
